import React from 'react';
import ReactDOM from 'react-dom';
import _ from 'underscore';

import { ErrorMessages } from '@bingads-webui-react/validation';
import { ProgressIndicator } from '@bingads-webui-react/fabric-control';

import {
  I18nModel, IFileItem, IFileUploadProgressInfo, IFileUploadCompletedInfo,
} from './types';

export interface IUploadProgressDescriptionOptions {
  i18n: I18nModel;
  progress: number;
  total: number;
}

export const getUploadProgressDescription = ({
  i18n, progress, total,
}: IUploadProgressDescriptionOptions): string => {
  /* istanbul ignore else */
  if (progress > 0 && total > 0) {
    const numberFormatted = i18n.formatPercent(progress);
    const totalFormatted = i18n.formatUnit(total / 1000, 'digital-kilobyte', { form: 'narrow' });
    return i18n.getString('{{number}} of {{total}}', { number: numberFormatted, total: totalFormatted });
  }
  return '';
};

export type FilePickerProgressProps = {
  i18n: I18nModel;
  filePickerCssSelector?: string;
  fileList?: IFileItem[] | Record<string, IFileItem>;
  filesUploadProgressInfo?: Record<string, IFileUploadProgressInfo>;
  filesUploadCompletedInfo?: Record<string, IFileUploadCompletedInfo>;
};

export const FilePickerProgress: React.FC<FilePickerProgressProps> = ({
  i18n, filePickerCssSelector, fileList, filesUploadProgressInfo, filesUploadCompletedInfo,
}: FilePickerProgressProps) => _.map(fileList, (file: IFileItem) => {
  const { progress: uploadProgress, total, error: uploadError } =
    filesUploadProgressInfo[file.id] || {};
  const { success: completedSuccess } = filesUploadCompletedInfo[file.id] || {};
  const filePickerElement = document.querySelector(`${filePickerCssSelector} [data-file-id="${file.id}"]`);

  if (!filePickerElement) {
    return null;
  }

  if (uploadError || completedSuccess === false) {
    return ReactDOM.createPortal(
      <ErrorMessages messages={[i18n.getString('FileUpload_Failed')]} />,
      filePickerElement
    );
  }

  if (completedSuccess) {
    return ReactDOM.createPortal(
      <ProgressIndicator percentComplete={1} description={i18n.getString('FileUpload_Succeed')} />,
      filePickerElement
    );
  }

  if (uploadProgress > 0) {
    const description = getUploadProgressDescription({ i18n, progress: uploadProgress, total });
    return ReactDOM.createPortal(
      <ProgressIndicator
        percentComplete={uploadProgress < 1 ? uploadProgress : undefined}
        description={description}
      />,
      filePickerElement
    );
  }

  return null;
}) as unknown as React.ReactElement;

FilePickerProgress.defaultProps = {
  filePickerCssSelector: '',
  fileList: {},
  filesUploadProgressInfo: {},
  filesUploadCompletedInfo: {},
};
